import { categoryMetadata } from '@/config/categories';
import { seedNews } from '@/data/seedNews';
import type { Category, CategorySummary, NewsArticle } from '@/types/news';
import type { SearchFilters, SearchResult } from '@/types/search';
import { createSlug } from '@/utils/slug';

const publishedArticles = seedNews
  .filter((article) => article.status === 'published')
  .sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime());

export function getPublishedNews(): NewsArticle[] {
  return publishedArticles;
}

export function getBreakingNews(): NewsArticle[] {
  return publishedArticles.filter((article) => article.breaking);
}

export function getFeaturedNews(): NewsArticle[] {
  return publishedArticles.filter((article) => article.featured);
}

export function getArticleBySlug(slug: string): NewsArticle | undefined {
  return publishedArticles.find((article) => article.slug === slug);
}

export function getNewsByCategory(category: Category): NewsArticle[] {
  return publishedArticles.filter((article) => article.category === category);
}

export function getRelatedNews(article: NewsArticle, limit = 3): NewsArticle[] {
  return publishedArticles
    .filter((item) => item.id !== article.id)
    .map((item) => ({
      item,
      score:
        (item.category === article.category ? 8 : 0) +
        item.tags.filter((tag) => article.tags.includes(tag)).length * 3 +
        item.people.filter((person) => article.people.includes(person)).length * 2
    }))
    .sort((a, b) => b.score - a.score || b.item.views - a.item.views)
    .slice(0, limit)
    .map(({ item }) => item);
}

export function getCategorySummaries(): CategorySummary[] {
  return categoryMetadata.map((category) => ({
    ...category,
    count: publishedArticles.filter((article) => article.category === category.name).length
  }));
}

export function searchNews({ query, category, date }: SearchFilters): SearchResult[] {
  const normalizedQuery = normalize(query);
  const normalizedCategory = category ? createSlug(category) : '';
  const normalizedDate = date ? date.trim() : '';

  return publishedArticles
    .map((article) => {
      const matchedFields: string[] = [];
      let score = 0;

      if (normalizedCategory && createSlug(article.category) !== normalizedCategory) {
        return null;
      }

      if (normalizedDate && !article.publishedAt.startsWith(normalizedDate)) {
        return null;
      }

      if (!normalizedQuery) {
        return { article, score: article.featured ? 3 : 1, matchedFields: ['recientes'] };
      }

      const fields = [
        { key: 'titulo', value: article.title, weight: 10 },
        { key: 'categoria', value: article.category, weight: 8 },
        { key: 'lugar', value: article.place, weight: 7 },
        { key: 'persona', value: article.people.join(' '), weight: 7 },
        { key: 'fecha', value: article.publishedAt, weight: 6 },
        { key: 'resumen', value: article.summary, weight: 5 },
        { key: 'texto', value: article.body, weight: 3 },
        { key: 'etiquetas', value: article.tags.join(' '), weight: 4 }
      ];

      for (const field of fields) {
        if (normalize(field.value).includes(normalizedQuery)) {
          score += field.weight;
          matchedFields.push(field.key);
        }
      }

      return score > 0 ? { article, score, matchedFields } : null;
    })
    .filter((result): result is SearchResult => result !== null)
    .sort((a, b) => b.score - a.score || b.article.views - a.article.views);
}

function normalize(value: string) {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}
